'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { useAuth } from '@repo/auth';
import { RightPanel, rightPanelIds, useRightPanel } from '@repo/features/right-panel';
import type { DuplicateContainer } from '@repo/schema/page-data';
import { useQueryClient } from '@repo/tanstack/query';
import { usePathname } from 'next/navigation';
import { createContext, type PropsWithChildren, use, useRef, useState } from 'react';
import { useDuplicateContainer } from '@/app/@protected/loads/_hooks/use-duplicate-container.ts';
import { useLoadCreatedToast } from '@/app/@protected/loads/_hooks/use-load-created-toast.tsx';
import { type DuplicateRecord, EMPTY_DUPLICATE_RECORD, leavePath, recordDuplicate } from '@/app/@protected/loads/_utils/duplicate-record.ts';
import { type DuplicateOrigin, getOriginalStops } from '@/app/@protected/loads/_utils/duplicate-card.ts';
import { type DuplicateSaveOutcome, getCreatedLoadIds, getUnconfirmedCount } from '@/app/@protected/loads/_utils/duplicate-save.ts';
import { type CarriedDuplicate, DuplicateFlow } from './duplicate-flow.tsx';
type DuplicateContextValue = {
  openDuplicate: (_loadId: number) => void;
  /** The Duplicates created in this tab, which the loads list highlights. */
  record: DuplicateRecord;
  /** A duplication session shows: the dialog, or the side panel while it is open. */
  sessionOpen: boolean;
};
type DuplicateSession = {
  carried?: CarriedDuplicate;
  container: DuplicateContainer;
  id: number;
  loadId: number;
  origin: Promise<DuplicateOrigin>;
};
const DuplicateContext = createContext<DuplicateContextValue | null>(null);
export const useDuplicate = () => {
  const context = use(DuplicateContext);
  if (!context) throw new Error('useDuplicate must be used within DuplicateProvider');
  return context;
};

// One signed-in User's duplication: the open session, the container chosen in this tab and the
// record of the Duplicates created in it.
const UserDuplicateProvider = t0 => {
  const $ = _c(32);
  const {
    children
  } = t0;
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    container: lastContainer,
    rememberContainer
  } = useDuplicateContainer();
  const {
    closePanel,
    isOpen: panelOpen,
    openPanel
  } = useRightPanel(rightPanelIds.duplicateLoad);
  const [session, setSession] = useState(null);
  const latestIdRef = useRef(0);
  const {
    showDuplicatesCreatedToast
  } = useLoadCreatedToast();
  const [record, setRecord] = useState(EMPTY_DUPLICATE_RECORD);
  const pathname = usePathname();
  const [recordPathname, setRecordPathname] = useState(pathname);
  if (pathname !== recordPathname) {
    setRecordPathname(pathname);
    setRecord(previous => leavePath(previous, recordPathname, pathname));
  }
  let t1;
  if ($[0] !== api || $[1] !== queryClient) {
    t1 = async loadId => {
      const [template, originalLoad] = await Promise.all([queryClient.fetchQuery(api.loads.duplicate.template.queryOptions({
        path: {
          loadId
        }
      }, {
        staleTime: 0
      })), queryClient.fetchQuery(api.loads.get.full.queryOptions({
        path: {
          id: loadId
        }
      }, {
        staleTime: 0
      }))]);
      return {
        originalStops: getOriginalStops(template, originalLoad.drops),
        template
      };
    };
    $[0] = api;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const readOrigin = t1;
  let t2;
  if ($[3] !== showDuplicatesCreatedToast) {
    t2 = loadId_0 => showDuplicatesCreatedToast([loadId_0], 1);
    $[3] = showDuplicatesCreatedToast;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const showCreated = t2;
  let t3;
  if ($[5] !== showDuplicatesCreatedToast) {
    t3 = outcomes => {
      const createdLoadIds = getCreatedLoadIds(outcomes);
      if (createdLoadIds.length) {
        showDuplicatesCreatedToast(createdLoadIds, outcomes.length, getUnconfirmedCount(outcomes));
      }
    };
    $[5] = showDuplicatesCreatedToast;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const announce = t3;
  let t4;
  if ($[7] !== closePanel || $[8] !== openPanel) {
    t4 = next => {
      setSession(next);
      if (next.container === "panel") {
        openPanel();
      } else {
        closePanel();
      }
    };
    $[7] = closePanel;
    $[8] = openPanel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const show = t4;
  let t5;
  if ($[10] !== announce || $[11] !== children || $[12] !== closePanel || $[13] !== lastContainer || $[14] !== panelOpen || $[15] !== readOrigin || $[16] !== record || $[17] !== rememberContainer || $[18] !== session || $[19] !== show || $[20] !== showCreated) {
    const renderFlow = current => <DuplicateFlow carried={current.carried} container={current.container} key={current.id} loadId={current.loadId} onClose={outcomes_0 => {
      announce(outcomes_0);
      if (latestIdRef.current !== current.id) {
        return;
      }
      if (current.container === "panel") {
        closePanel();
      } else {
        setSession(null);
      }
    }} onCreated={showCreated} onEachCreated={loadId_1 => setRecord(previous_0 => recordDuplicate(previous_0, loadId_1))} onSwitch={carried => {
      const container = current.container === "dialog" ? "panel" : "dialog";
      rememberContainer(container);
      show({
        ...current,
        carried,
        container
      });
    }} origin={current.origin} data-sentry-element="DuplicateFlow" data-sentry-component="renderFlow" data-sentry-source-file="duplicate-provider.tsx" />;
    let t6;
    if ($[22] !== lastContainer || $[23] !== readOrigin || $[24] !== show) {
      t6 = loadId_2 => {
        latestIdRef.current = latestIdRef.current + 1;
        show({
          container: lastContainer,
          id: latestIdRef.current,
          loadId: loadId_2,
          origin: readOrigin(loadId_2)
        });
      };
      $[22] = lastContainer;
      $[23] = readOrigin;
      $[24] = show;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    const t7 = session !== null && (session.container === "dialog" || panelOpen);
    let t8;
    if ($[26] !== record || $[27] !== t6 || $[28] !== t7) {
      t8 = {
        openDuplicate: t6,
        record,
        sessionOpen: t7
      };
      $[26] = record;
      $[27] = t6;
      $[28] = t7;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    const t9 = session?.container === "panel" ? renderFlow(session) : null;
    let t10;
    if ($[30] !== t9) {
      t10 = <RightPanel id={rightPanelIds.duplicateLoad} title="Duplicate load" width="min(calc(402px + 1.5rem), 100vw)" data-sentry-element="RightPanel" data-sentry-source-file="duplicate-provider.tsx">{t9}</RightPanel>;
      $[30] = t9;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    t5 = <DuplicateContext value={t8} data-sentry-element="DuplicateContext" data-sentry-component="UserDuplicateProvider" data-sentry-source-file="duplicate-provider.tsx">{children}{t10}{session?.container === "dialog" ? renderFlow(session) : null}</DuplicateContext>;
    $[10] = announce;
    $[11] = children;
    $[12] = closePanel;
    $[13] = lastContainer;
    $[14] = panelOpen;
    $[15] = readOrigin;
    $[16] = record;
    $[17] = rememberContainer;
    $[18] = session;
    $[19] = show;
    $[20] = showCreated;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};

/**
 * Opens the duplication flow for an Original Load in place, from any page, in the container the
 * User last used, and records every Duplicate it creates. Mount it inside `RightPanelProvider`. Its
 * state, the record included, belongs to the signed-in User, so it starts afresh when they change,
 * as when an impersonation starts or stops without a page load.
 */
export const DuplicateProvider = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  const {
    user
  } = useAuth();
  const t1 = user?.id ?? "signed-out";
  let t2;
  if ($[0] !== children || $[1] !== t1) {
    t2 = <UserDuplicateProvider key={t1} data-sentry-element="UserDuplicateProvider" data-sentry-component="DuplicateProvider" data-sentry-source-file="duplicate-provider.tsx">{children}</UserDuplicateProvider>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};